import { BaseSelectorOption } from "@/types/components/selector.types";
import { Box, Stack, SxProps } from "@mui/material";
import { Theme } from "klayowebshared";
import { Component } from "react";
import { withLocale, WithLocaleProps } from "../../providers/LocaleProvider";
import AddIcon from "../../resources/images/icons-add.svg";
import DeleteIcon from "../../resources/images/icons-delete.svg";
import { Button } from "../Button";
import { SelectMenu } from "../SelectMenu";

export interface RepeatingSelectProps<
  TOption extends BaseSelectorOption = BaseSelectorOption
> extends WithLocaleProps {
  maxRows?: number;
  defaultItem: TOption;
  items: Record<string, TOption>;
  values?: TOption[];
  onChange?: (values: TOption[]) => void;
  itemComparator?: (e1: TOption, e2: TOption) => boolean;
  selectSx?: SxProps;
  sx?: SxProps;
  theme?: typeof Theme;
}

interface RepeatingSelectState<TOption extends BaseSelectorOption = BaseSelectorOption> {
  values: TOption[];
}

class RepeatingSelectBase<
  TOption extends BaseSelectorOption = BaseSelectorOption
> extends Component<RepeatingSelectProps<TOption>, RepeatingSelectState<TOption>> {
  static defaultProps = {
    maxRows: 4
  };

  constructor(props: RepeatingSelectProps<TOption>) {
    super(props);

    this.state = {
      values: props.values && props.values.length !== 0 ? props.values : [props.defaultItem]
    };
  }

  onAddRow() {
    const { defaultItem, onChange } = this.props;
    const { values } = this.state;

    values.push(defaultItem);
    this.setState({ values });

    if (onChange) onChange(values);
  }

  updateValue(index: any, newValue: any) {
    const { values } = this.state;
    values[index] = newValue;
    this.forceUpdate();
  }

  onDeleteRow(index: any) {
    const { onChange } = this.props;
    const { values } = this.state;

    values.splice(index, 1);
    this.forceUpdate();

    if (onChange) onChange(values);
  }

  onRowChange(index: any, newValue: any) {
    const { onChange } = this.props;
    const { values } = this.state;

    this.updateValue(index, newValue);

    if (onChange) onChange(values);
  }

  renderRow(value: any, index: number) {
    const { theme, defaultItem, items, maxRows, itemComparator, selectSx, translate } = this.props;
    const { values } = this.state;
    const notificationLabel = translate?.("notifications.notification") ?? "Notification";

    return (
      <Stack
        key={index}
        className='klayo-repeatingselector_row'
        direction='row'
        spacing={1}
        sx={{ height: { xs: "66px", md: "42px" } }}
      >
        {theme.isBreakpointUp("md") && (
          <Box
            className='klayo-repeatingselector_row_label'
            sx={{
              display: { xs: "none", md: "block" }
            }}
          >
            {notificationLabel}
          </Box>
        )}

        <SelectMenu<TOption>
          value={value}
          defaultValue={defaultItem}
          clearable={false}
          label={theme.isBreakpointDown("md") ? notificationLabel : null}
          //firstValueDefault={true}
          //dense={true}
          items={Object.entries(items)} //.filter((i1, key) => itemComparator ? !values.find(i2 => itemComparator(i1[1], i2)) : true)
          onChange={(e) => this.onRowChange(index, e.target.value)}
          disableItem={(i1) => Boolean(values.find((i2) => itemComparator?.(i1, i2)))}
          sx={{ width: "100%", minWidth: "150px", ...selectSx }}
        />

        <Stack
          direction='row'
          spacing={1}
          alignItems='center'
          sx={{ paddingTop: { xs: "24px", md: "0" } }}
        >
          {values.length > 1 && (
            <Button onClick={(e: any) => this.onDeleteRow(index)} size='sm' icon={<DeleteIcon />} />
          )}

          {index === values.length - 1 && value !== defaultItem && (
            <Button
              onClick={this.onAddRow.bind(this)}
              disabled={index >= (maxRows || RepeatingSelectBase.defaultProps.maxRows - 1)}
              size='sm'
              icon={<AddIcon />}
            />
          )}
        </Stack>
      </Stack>
    );
  }

  render() {
    const { defaultItem, sx } = this.props;
    const { values } = this.state;

    return (
      <Stack
        className='klayo-repeatingselector'
        direction='column'
        spacing={{ xs: 3, md: 4 }}
        sx={sx}
      >
        {Object.values(values).map((v, index) => this.renderRow(v, index))}
      </Stack>
    );
  }
}

export const RepeatingSelect = withLocale(RepeatingSelectBase);
